import { useEffect, useState } from 'react';
import { useTheme } from '@/components/ThemeProvider';
import { Moon, Sun } from 'lucide-react';
import useObfuscatedEmail from '@/hooks/useObfuscatedEmail';
import { t } from '@/data/translations';
import type { Lang } from '@/pages/Index';
interface StickyHeaderProps {
  lang: Lang;
  onLangChange: (lang: Lang) => void;
}

const StickyHeader = ({ lang, onLangChange }: StickyHeaderProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const { theme, setTheme } = useTheme();
  const { handleEmailClick } = useObfuscatedEmail();
  const tr = t(lang).stickyHeader;
  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past the hero section (roughly 80vh)
      const scrollThreshold = window.innerHeight * 0.8;
      setIsVisible(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Determine if we're in dark mode (accounting for system preference)
  const isDark = theme === 'dark' || 
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        isVisible 
          ? 'translate-y-0 opacity-100' 
          : '-translate-y-full opacity-0 pointer-events-none'
      }`}
    >
      <div className="bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <span className="text-sm font-medium text-foreground tracking-tight">
            Allen Le Yaouanc
          </span>
          
          <div className="flex items-center gap-5">
            {/* Language switcher */}
            <div className="flex items-center gap-1 text-xs tracking-wider">
              <button
                onClick={() => onLangChange('fr')}
                className={`px-1.5 py-0.5 transition-colors duration-300 cursor-pointer ${
                  lang === 'fr'
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground/50 hover:text-muted-foreground'
                }`}
              >
                FR
              </button>
              <span className="text-muted-foreground/30">/</span>
              <button
                onClick={() => onLangChange('en')}
                className={`px-1.5 py-0.5 transition-colors duration-300 cursor-pointer ${
                  lang === 'en'
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground/50 hover:text-muted-foreground'
                }`}
              >
                EN
              </button>
            </div>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 -m-2 text-muted-foreground hover:text-foreground transition-colors duration-300"
              aria-label={isDark ? tr.darkLabel : tr.lightLabel}
            >
              {isDark ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>

            {/* Contact link */}
            <a
              href="#"
              onClick={handleEmailClick}
              className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors duration-300 group"
            >
              <span className="link-hover">Contact</span>
              <svg 
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M17 8l4 4m0 0l-4 4m4-4H3" 
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default StickyHeader;
